<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TemplateHeaderMediaAPI from 'dashboard/api/templateHeaderMedia';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CurrencyInput from 'dashboard/components-next/input/CurrencyInput.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import {
  AVAILABILITIES,
  CONDITIONS,
  emptyProduct,
  isHttpsUrl,
  optionKey,
  productImageError,
  productPayload,
  productToForm,
  validateProduct,
} from './catalogUtils';

defineProps({
  isSaving: { type: Boolean, default: false },
});
const emit = defineEmits(['save', 'error']);

const { t } = useI18n();
const dialogRef = ref(null);
const fileInputRef = ref(null);
const form = ref(emptyProduct());
const errors = ref({});
const isUploading = ref(false);
const uploadToken = ref(0);

const isNew = computed(() => !form.value.id);
const title = computed(() =>
  isNew.value
    ? t('WHATSAPP_CATALOGS.PRODUCT_FORM.CREATE_TITLE')
    : t('WHATSAPP_CATALOGS.PRODUCT_FORM.EDIT_TITLE')
);
const availabilityOptions = computed(() =>
  AVAILABILITIES.map(value => ({
    value,
    label: t(`WHATSAPP_CATALOGS.PRODUCTS.AVAILABILITY.${optionKey(value)}`),
  }))
);
const conditionOptions = computed(() =>
  CONDITIONS.map(value => ({
    value,
    label: t(`WHATSAPP_CATALOGS.PRODUCTS.CONDITION.${optionKey(value)}`),
  }))
);
const imagePreview = computed(() =>
  isHttpsUrl(form.value.image_url) ? form.value.image_url.trim() : ''
);

const errorMessage = field =>
  errors.value[field]
    ? t(`WHATSAPP_CATALOGS.PRODUCT_FORM.ERRORS.${errors.value[field]}`)
    : '';
const messageType = field => (errors.value[field] ? 'error' : 'info');

const open = (product = null) => {
  form.value = product ? productToForm(product) : emptyProduct();
  errors.value = {};
  isUploading.value = false;
  uploadToken.value += 1;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

// Descarta uploads em andamento ao fechar para não preencher outro produto.
const onClose = () => {
  uploadToken.value += 1;
  isUploading.value = false;
};

const submit = () => {
  errors.value = validateProduct(form.value, { isNew: isNew.value });
  if (Object.keys(errors.value).length) return;
  emit('save', { id: form.value.id, payload: productPayload(form.value) });
};

const pickImage = () => fileInputRef.value?.click();

const uploadImage = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  const fileError = productImageError(file);
  if (fileError) {
    errors.value = { ...errors.value, image_url: fileError };
    return;
  }
  uploadToken.value += 1;
  const token = uploadToken.value;
  isUploading.value = true;
  try {
    const { data } = await TemplateHeaderMediaAPI.upload(file, 'IMAGE');
    if (token !== uploadToken.value) return;
    form.value.image_url = data.url;
    errors.value = { ...errors.value, image_url: undefined };
    useAlert(t('WHATSAPP_CATALOGS.PRODUCT_FORM.UPLOAD_SUCCESS'));
  } catch (error) {
    if (token !== uploadToken.value) return;
    emit('error', {
      title: t('WHATSAPP_CATALOGS.ISSUES.UPLOAD_ERROR_TITLE'),
      message:
        error?.response?.data?.error || t('WHATSAPP_CATALOGS.ACTION_ERROR'),
    });
  } finally {
    if (token === uploadToken.value) isUploading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="3xl"
    solid
    overflow-y-auto
    sticky-header-footer
    :title="title"
    :description="t('WHATSAPP_CATALOGS.PRODUCT_FORM.DESCRIPTION')"
    :confirm-button-label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.SAVE')"
    :is-loading="isSaving"
    :disable-confirm-button="isUploading"
    :close-on-click-outside="false"
    @confirm="submit"
    @close="onClose"
  >
    <div class="grid grid-cols-1 gap-4 py-1 md:grid-cols-2">
      <Input
        v-model="form.retailer_id"
        :label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.RETAILER_ID')"
        :disabled="!isNew"
        :message="
          errorMessage('retailer_id') ||
          (isNew ? '' : t('WHATSAPP_CATALOGS.PRODUCT_FORM.RETAILER_ID_HELP'))
        "
        :message-type="messageType('retailer_id')"
        placeholder="SKU-001"
      />
      <Input
        v-model="form.name"
        :label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.NAME')"
        :message="errorMessage('name')"
        :message-type="messageType('name')"
      />
      <div class="md:col-span-2">
        <TextArea
          v-model="form.description"
          :label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.DESCRIPTION_FIELD')"
          :max-length="5000"
          show-character-count
          auto-height
          :message="errorMessage('description')"
          :message-type="messageType('description')"
        />
      </div>
      <CurrencyInput
        v-model="form.price"
        :label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.PRICE')"
        :message="errorMessage('price')"
        :message-type="messageType('price')"
      />
      <div class="grid grid-cols-2 gap-3">
        <label
          class="mb-0 flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('WHATSAPP_CATALOGS.PRODUCT_FORM.AVAILABILITY') }}
          <NextSelect
            v-model="form.availability"
            :options="availabilityOptions"
            :error="errorMessage('availability')"
            class="w-full [&>select]:w-full"
          />
        </label>
        <label
          class="mb-0 flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('WHATSAPP_CATALOGS.PRODUCT_FORM.CONDITION') }}
          <NextSelect
            v-model="form.condition"
            :options="conditionOptions"
            :error="errorMessage('condition')"
            class="w-full [&>select]:w-full"
          />
        </label>
      </div>
      <div class="flex flex-col gap-2 md:col-span-2">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div class="min-w-0 flex-1">
            <Input
              v-model="form.image_url"
              type="url"
              :label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.IMAGE_URL')"
              :message="
                errorMessage('image_url') ||
                t('WHATSAPP_CATALOGS.PRODUCT_FORM.IMAGE_UPLOAD_HELP')
              "
              :message-type="messageType('image_url')"
              placeholder="https://"
            />
          </div>
          <NextButton
            type="button"
            variant="faded"
            color="slate"
            size="sm"
            class="shrink-0 sm:mt-7"
            icon="i-hugeicons-image-upload"
            :label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.IMAGE_UPLOAD')"
            :is-loading="isUploading"
            @click="pickImage"
          />
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            accept="image/jpeg,image/png"
            @change="uploadImage"
          />
        </div>
        <img
          v-if="imagePreview"
          :src="imagePreview"
          :alt="t('WHATSAPP_CATALOGS.PRODUCT_FORM.IMAGE_PREVIEW')"
          class="size-24 rounded-lg border border-n-weak bg-n-alpha-1 object-contain"
        />
      </div>
      <Input
        v-model="form.url"
        type="url"
        :label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.URL')"
        :message="errorMessage('url')"
        :message-type="messageType('url')"
        placeholder="https://"
      />
      <Input
        v-model="form.brand"
        :label="t('WHATSAPP_CATALOGS.PRODUCT_FORM.BRAND')"
        :message="errorMessage('brand')"
        :message-type="messageType('brand')"
      />
    </div>
  </Dialog>
</template>
